import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ErpProvider } from "@/context/ErpContext";
import AppLayout from "@/components/layout/AppLayout";

// ── Auth pages ────────────────────────────────────────────────────────────────
import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPass";
import ChangePasswordPage from "@/pages/ChangePasswordPage";
// RegisterPage intentionally removed — admin creates users via UsersPage

// ── App pages ─────────────────────────────────────────────────────────────────
import DashboardPage from "@/pages/DashboardPage";
import FixedDepositsPage from "@/pages/FixedDepositsPage";
import LoansPage from "@/pages/LoansPage";
import TransactionsPage from "@/pages/TransactionsPage";
import FxPage from "@/pages/FxPage";
import BankAccountsPage from "@/pages/BankAccountsPage";
import ReconciliationPage from "@/pages/ReconciliationPage";
import ApprovalsPage from "@/pages/ApprovalsPage";
import BankGuaranteePage from "@/pages/BankGuaranteePage";
import LetterOfCreditPage from "@/pages/LetterOfCreditPage";
import AutomationsPage from "@/pages/AutomationsPage";
import EmailAgentPage from "@/pages/EmailAgentPage";

// ── Admin pages ───────────────────────────────────────────────────────────────
import AgentSetupPage from "@/pages/admin/AgentSetupPage";
import UsersPage from "@/pages/admin/UsersPage";
import UserAgentsPage from "@/pages/admin/UserAgentsPage";
import LlmSettingsPage from "@/pages/admin/LlmSettingsPage";
import VakuaiDataExplorerPage from "@/pages/admin/VakuaiDataExplorerPage";

// ── ALM pages ─────────────────────────────────────────────────────────────────
import AlmDashboardPage from "@/pages/alm/AlmDashboardPage";
import AssetsMapPage from "@/pages/alm/AssetsMapPage";
import ProductMasterPage from "@/pages/alm/ProductMasterPage";
import SiteMasterPage from "@/pages/alm/SiteMasterPage";
import LocationMasterPage from "@/pages/alm/LocationMasterPage";
import PlanToProcurePage from "@/pages/alm/PlanToProcurePage";
import ReceivingProcessPage from "@/pages/alm/ReceivingProcessPage";
import AssetTrackingPage from "@/pages/alm/AssetTrackingPage";
import MovementOfItemsPage from "@/pages/alm/MovementOfItemsPage";
import AssetCapitalizationPage from "@/pages/alm/AssetCapitalizationPage";
import AssetMaintenancePage from "@/pages/alm/AssetMaintenancePage";
import AssetRetirementPage from "@/pages/alm/AssetRetirementPage";

// ── Telecom pages ─────────────────────────────────────────────────────────────
import { FinanceBssPage, RevenueAssurancePage, SpectrumPage, SlicingPage } from "@/pages/telecom/TelecomPages";

// ── Marketing pages ───────────────────────────────────────────────────────────
import MarketingLayout from "@/components/marketing/MarketingLayout";
import HomePage from "@/pages/marketing/HomePage";
import ProductsPage from "@/pages/marketing/ProductsPage";
import UseCasesPage from "@/pages/marketing/UseCasesPage";
import PartnersPage from "@/pages/marketing/PartnersPage";

// ─────────────────────────────────────────────────────────────────────────────

function Marketing({ children }) {
  return <MarketingLayout>{children}</MarketingLayout>;
}

/**
 * Protected — requires authentication.
 *
 * is_first_login=true  → redirect to /change-password before anything else.
 * adminOnly=true       → only admin / product_admin roles can access.
 */
function Protected({ children, adminOnly = false }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-xs tracking-[0.2em] uppercase font-bold text-gray-500">
        Loading…
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  // First-login guard — must change password before doing anything
  if (user.is_first_login) return <Navigate to="/change-password" replace />;

  // Admin-only guard
  if (adminOnly) {
    const isAdmin = user.is_admin || user.role === "admin" || user.role === "product_admin";
    if (!isAdmin) return <Navigate to="/dashboard" replace />;
  }

  return <AppLayout>{children}</AppLayout>;
}

/**
 * PublicOnly — redirect already-authenticated users away from login.
 * If they still need to change password, send them there first.
 */
function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) {
    return <Navigate to={user.is_first_login ? "/change-password" : "/dashboard"} replace />;
  }
  return children;
}

/**
 * Standalone /change-password route.
 * Requires auth but renders WITHOUT AppLayout (full-screen form).
 * If password already changed, redirect to dashboard.
 */
function ChangePasswordRoute() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_first_login) return <Navigate to="/dashboard" replace />;
  return <ChangePasswordPage />;
}

// ─────────────────────────────────────────────────────────────────────────────

export default function App() {
  return (
    <AuthProvider>
      <ErpProvider>
        <BrowserRouter>
          <Routes>
            {/* ── Marketing ── */}
            <Route path="/"           element={<Marketing><HomePage /></Marketing>} />
            <Route path="/products"   element={<Marketing><ProductsPage /></Marketing>} />
            <Route path="/use-cases"  element={<Marketing><UseCasesPage /></Marketing>} />
            <Route path="/partners"   element={<Marketing><PartnersPage /></Marketing>} />

            {/* ── Auth ── */}
            <Route path="/login"           element={<PublicOnly><LoginPage /></PublicOnly>} />
            <Route path="/forgot-password" element={<PublicOnly><ForgotPasswordPage /></PublicOnly>} />
            <Route path="/change-password" element={<ChangePasswordRoute />} />
            {/* /register removed — no self-registration */}

            {/* ── App (authenticated) ── */}
            <Route path="/dashboard"         element={<Protected><DashboardPage /></Protected>} />
            <Route path="/fixed-deposits"    element={<Protected><FixedDepositsPage /></Protected>} />
            <Route path="/loans"             element={<Protected><LoansPage /></Protected>} />
            <Route path="/transactions"      element={<Protected><TransactionsPage /></Protected>} />
            <Route path="/fx"                element={<Protected><FxPage /></Protected>} />
            <Route path="/bank-accounts"     element={<Protected><BankAccountsPage /></Protected>} />
            <Route path="/reconciliation"    element={<Protected><ReconciliationPage /></Protected>} />
            <Route path="/approvals"         element={<Protected><ApprovalsPage /></Protected>} />
            <Route path="/bank-guarantees"   element={<Protected><BankGuaranteePage /></Protected>} />
            <Route path="/letters-of-credit" element={<Protected><LetterOfCreditPage /></Protected>} />
            <Route path="/automations"       element={<Protected><AutomationsPage /></Protected>} />
            <Route path="/email-agent"       element={<Protected><EmailAgentPage /></Protected>} />

            {/* ── Admin (admin / product_admin only) ── */}
            <Route path="/admin/agent-setup"       element={<Protected adminOnly><AgentSetupPage /></Protected>} />
            <Route path="/admin/users"             element={<Protected adminOnly><UsersPage /></Protected>} />
            <Route path="/admin/user-agents/:userId" element={<Protected adminOnly><UserAgentsPage /></Protected>} />
            <Route path="/admin/llm-settings"      element={<Protected adminOnly><LlmSettingsPage /></Protected>} />
            <Route path="/admin/database"          element={<Protected adminOnly><VakuaiDataExplorerPage /></Protected>} />

            {/* ── ALM ── */}
            <Route path="/alm/dashboard"         element={<Protected><AlmDashboardPage /></Protected>} />
            <Route path="/alm/map"               element={<Protected><AssetsMapPage /></Protected>} />
            <Route path="/alm/products"          element={<Protected><ProductMasterPage /></Protected>} />
            <Route path="/alm/sites"             element={<Protected><SiteMasterPage /></Protected>} />
            <Route path="/alm/locations"         element={<Protected><LocationMasterPage /></Protected>} />
            <Route path="/alm/procurement-plans" element={<Protected><PlanToProcurePage /></Protected>} />
            <Route path="/alm/receipts"          element={<Protected><ReceivingProcessPage /></Protected>} />
            <Route path="/alm/assets"            element={<Protected><AssetTrackingPage /></Protected>} />
            <Route path="/alm/movements"         element={<Protected><MovementOfItemsPage /></Protected>} />
            <Route path="/alm/capitalizations"   element={<Protected><AssetCapitalizationPage /></Protected>} />
            <Route path="/alm/maintenance"       element={<Protected><AssetMaintenancePage /></Protected>} />
            <Route path="/alm/retirements"       element={<Protected><AssetRetirementPage /></Protected>} />

            {/* ── Telecom ── */}
            <Route path="/telecom/finance-bss"       element={<Protected><FinanceBssPage /></Protected>} />
            <Route path="/telecom/revenue-assurance" element={<Protected><RevenueAssurancePage /></Protected>} />
            <Route path="/telecom/spectrum"          element={<Protected><SpectrumPage /></Protected>} />
            <Route path="/telecom/slicing"           element={<Protected><SlicingPage /></Protected>} />

            {/* ── Fallback ── */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </ErpProvider>
    </AuthProvider>
  );
}